import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { RequireAuth, useDistributionPrincipal } from '@dss/auth'
import { Banner, Button } from '@dss/design-system'
import { AdminShell } from '../components/AdminShell'
import { OnboardingWizard } from '../features/onboarding/OnboardingWizard'
import { usePortalSession } from '../features/session/usePortalSession'

/** /onboarding — E3.3 / D19 §D19-3. Transient onboarding wizard для OWNER'а.
 *  В sidebar намеренно НЕ показываем — entry-point ведёт через авто-редирект
 *  с /dashboard для OWNER'а с незавершённым wizard'ом. `active="onboarding"`
 *  оставляет sidebar без подсветки (нет пункта меню для этого ключа), чтобы
 *  не «лгать» подсветкой Дашборда / других экранов.
 *
 *  E3.5: фронт-guard — не-OWNER (MANAGER / FIELD / PA) отправляется обратно
 *  на /dashboard. Backend-permission `distributor.settings.manage` всё равно
 *  блокирует /admin/onboarding/state с 403 для не-OWNER, но redirect даёт
 *  более user-friendly experience. */
export const Route = createFileRoute('/onboarding')({
  component: OnboardingScreen,
})

function OnboardingScreen() {
  return (
    <RequireAuth>
      <OnboardingContent />
    </RequireAuth>
  )
}

function OnboardingContent() {
  const principal = useDistributionPrincipal()
  const navigate = useNavigate()
  const session = usePortalSession()
  const isOwner = !session.isError && !session.isPending && session.data?.state === 'ACTIVE'
    && session.data.role === 'DISTRIBUTOR_OWNER'
    && session.data.permissions.includes('distributor.settings.manage')
  const isPlatformAdmin = Boolean(principal?.isPlatformAdmin)

  useEffect(() => {
    // principal=null ⇒ ещё грузится JWT, ничего не делаем.
    if (principal && !session.isPending && !session.isError && (!isOwner || isPlatformAdmin)) {
      navigate({ to: '/' })
    }
  }, [principal, isOwner, isPlatformAdmin, navigate, session.isPending, session.isError])

  return (
    <AdminShell active="onboarding" crumbs={<strong>Начало работы</strong>}>
      {session.isPending && <p role="status">Проверяем доступ…</p>}
      {session.isError && <div><Banner variant="warning" body="Не удалось проверить доступ. Данные подготовки скрыты." />
        <Button variant="secondary" disabled={session.isFetching} onClick={() => { void session.refetch() }}>Повторить проверку</Button></div>}
      {isOwner && <OnboardingWizard key={`${principal?.distributorId}:${principal?.userId}`} />}
    </AdminShell>
  )
}
